<!--
  Crawlable link around a node whose click navigates to a known page.

  Crawlers never click — they only follow `<a href>`. Navigation here runs through
  `navigate` event processors on click, so without this wrapper a built page has
  zero internal links. The wrapper renders a real, visible-content `<a href>` around
  the clicked element (no hidden link — the anchor IS the control the visitor sees)
  and cancels the browser's own navigation, so the component's existing handler
  chain (validation, tracking, form submit, then `navigateTo`) stays the only thing
  that moves the visitor. Same mechanism as RouterLink's guardEvent.

  - STATIC (built pipe) mode only: the editor and previews render the slot bare.
  - `display: contents` keeps the layout identical — the anchor generates no box.
  - Before hydration, the inline capture script in builder/nuxt.config.ts cancels
    clicks on `a[data-pipe-nav]`; these handlers take over afterwards.
  - The href carries no query: SSR has none, the client has UTMs, so a query would
    mismatch on hydration. The real navigation keeps merging queries.
-->
<template>
  <a
    v-if="href"
    class="pipe-nav-link"
    :href="href"
    data-pipe-nav
    tabindex="-1"
    draggable="false"
    @click="onClick"
    @auxclick.prevent
    @contextmenu.prevent
  >
    <slot />
  </a>
  <slot v-else />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { PipeDisplayMode, usePipe } from './composables/usePipe'

const props = defineProps<{ pageId: string | null }>()

const pipe = usePipe()

const href = computed(() => {
  if (!props.pageId || pipe.currentMode.value !== PipeDisplayMode.STATIC) return null
  const page = pipe.getPageById(props.pageId)
  if (!page?.path) return null
  // Same path shape navigateToPageId builds, under the release's deploy path
  // (== RELEASE_PATH, frozen per release).
  const base = (pipe.constants.value['system.distributionPath'] as string | undefined) ?? '/'
  return `${base}/${page.path}/`.replace(/\/{2,}/g, '/')
})

// Bubble phase: the component's own click handler has already run by now.
// preventDefault only cancels the browser's navigation; it does not stop the
// event, so nothing else listening is affected.
function onClick(event: MouseEvent) {
  event.preventDefault()
}
</script>

<style scoped>
.pipe-nav-link {
  display: contents;
  /* UA link styles would otherwise reach the content through inheritance. */
  color: inherit;
  text-decoration: inherit;
  cursor: inherit;
  -webkit-touch-callout: none;
}
</style>
